<template>
  <div class="home-container">
    <!-- 左侧目录 - 固定高度占满 -->
    <div class="sidebar">
      <!-- Logo区域 -->
      <div class="logo-area">
        <img src="~@/assets/login/logo.png" alt="Logo" class="logo-img"/>
      </div>

      <el-menu
        background-color="black"
        text-color="#fff"
        active-text-color="#409EFF"
        unique-opened
        router
        :default-active="$route.path"
        class="sidebar-menu">
        <template v-for="item in visibleMenuList">
          <el-menu-item v-if="item.title === '首页'"
                        :index="item.path"
                        :key="item.menuId"
                        @click="saveNavState(item.path)">
            <div class="menu-title-container">
              <span
                v-html="formatIconPath(item.icon)"
                class="menu-icon">
              </span>
              <span slot="title">{{ item.title }}</span>
            </div>
          </el-menu-item>
          <el-submenu v-else
                      :index="item.menuId + ''"
                      :key="item.menuId">
            <template slot="title">
              <div class="menu-title-container">
                <span
                  v-html="formatIconPath(item.icon)"
                  class="menu-icon">
                </span>
                <span slot="title">{{ item.title }}</span>
              </div>
            </template>
            <el-menu-item
              v-for="subItem in getVisibleChildList(item.childList)"
              :index="subItem.path"
              :key="subItem.menuId"
              @click="saveNavState(subItem.path)">
              <div class="menu-title-container">
                <span
                  v-html="formatIconPath(subItem.icon)"
                  class="menu-icon">
                </span>
                <span slot="title">{{ subItem.title }}</span>
              </div>
            </el-menu-item>
          </el-submenu>
        </template>
      </el-menu>

      <!-- ICP备案号 - 放在左侧目录底部 -->
      <div class="sidebar-icp">
        <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer">
          粤ICP备2026058420号
        </a>
      </div>
    </div>

    <!-- 右侧区域 -->
    <div class="right-area">
      <!-- 右侧顶部固定区域：历史路由标签页 + 用户按钮 -->
      <div class="right-header">
        <!-- 历史路由标签页区域 -->
        <div class="history-tabs-wrapper">
          <div class="history-tabs">
            <!-- 首页永远固定在第一个 -->
            <div
              class="history-tab home-tab"
              :class="{ 'active': isHomeActive }"
              @click="navigateToHome">
              <div class="tab-content">
                <span class="tab-title">首页</span>
              </div>
            </div>

            <!-- 分隔符：首页与其他标签页之间 -->
            <span v-if="historyRoutes.length > 0" class="tab-separator">|</span>

            <!-- 其他历史记录 -->
            <template v-for="(route, index) in historyRoutes">
              <div
                :key="index"
                class="history-tab"
                :class="{ 'active': isRouteActive(route) }"
                @click="navigateToHistory(route)">
                <div class="tab-content">
                  <span class="tab-title">{{ route.title }}</span>
                  <span class="tab-close" @click.stop="removeHistoryItem(route)">
                    <i class="el-icon-close"></i>
                  </span>
                </div>
              </div>
              <!-- 在每个标签页之后添加分隔符（最后一个除外） -->
              <span
                v-if="index < historyRoutes.length - 1"
                :key="'sep-' + index"
                class="tab-separator">|</span>
            </template>

            <!-- 标签页操作下拉菜单 - 放在所有标签页的最后面 -->
            <el-dropdown trigger="click" @command="handleTabCommand" class="tab-dropdown" placement="bottom-end">
              <span class="tab-dropdown-trigger">
                <i class="el-icon-more"></i>
              </span>
              <el-dropdown-menu slot="dropdown" class="tab-dropdown-menu">
                <el-dropdown-item command="closeCurrent" :disabled="!currentRoutePath || currentRoutePath === '/welcome'">
                  <i class="el-icon-close"></i> 关闭当前页
                </el-dropdown-item>
                <el-dropdown-item command="closeOthers" :disabled="historyRoutes.length <= 1">
                  <i class="el-icon-circle-close"></i> 关闭其他
                </el-dropdown-item>
                <el-dropdown-item command="closeAll" :disabled="historyRoutes.length === 0">
                  <i class="el-icon-folder-delete"></i> 关闭所有
                </el-dropdown-item>
                <el-dropdown-item command="refresh" divided>
                  <i class="el-icon-refresh-right"></i> 刷新
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>

        <!-- 用户头像 - 始终固定在最右侧 -->
        <span class="user-account-header">{{ userAccount }}</span>
        <el-button class="avatar-button" @click="showUserinfo()">
          <img src="~@/assets/head/user.png" alt="User Avatar"/>
        </el-button>
      </div>

      <!-- 右侧内容区域 -->
      <div class="right-content">
        <keep-alive include="SerialBurnTool">
          <router-view :key="$route.path"></router-view>
        </keep-alive>
      </div>
    </div>

    <!-- 弹出对话框 -->
    <el-dialog
      :visible.sync="showUserinfoDialog"
      width="300px"
      @close="resetDialog"
      :modal="false">
      <!-- 自定义标题栏：左上角显示注销账号按钮 -->
      <div slot="title" class="dialog-title-custom">
        <el-button
          type="text"
          class="logout-title-btn"
          @click="deleteUserAccount">
          注销账号
        </el-button>
      </div>

      <div class="dialog-content">
        <img src="~@/assets/head/user.png" class="avatar-large" alt="Avatar"/>
        <!-- 账号显示在角色上方 -->
        <p class="user-account-text">{{ userAccount }}</p>
        <p class="user-role-text">{{ roleName }}</p>
        <div class="button-group">
          <el-button class="btn-padding" type="primary" @click="showResetPasswordDialog">重置密码</el-button>
          <el-button class="btn-padding" type="danger" @click="logout">退出登录</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 重置密码对话框 -->
    <el-dialog
      title="重置密码"
      :visible.sync="resetPasswordDialogVisible"
      width="400px"
      @close="resetPasswordDialogClosed"
      :modal="false">
      <el-form ref="resetPwdFormRef" :model="resetPwdForm" :rules="resetPwdRules" label-width="80px" class="reset-pwd-form">
        <el-form-item label="新密码" prop="password" class="pwd-form-item">
          <el-input v-model="resetPwdForm.password"
                    type="password"
                    clearable
                    show-password
                    placeholder="请输入新密码"></el-input>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword" class="pwd-form-item">
          <el-input v-model="resetPwdForm.confirmPassword"
                    type="password"
                    clearable
                    show-password
                    placeholder="请再次输入新密码"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button class="cancel-btn" @click="resetPasswordDialogVisible = false">取 消</el-button>
        <el-button class="confirm-btn" type="primary" @click="confirmResetPassword">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import WebSocketService from '@/components/websocket/webSocketService'
import JSEncrypt from 'jsencrypt'
import { Base64 } from 'js-base64'

export default {
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.resetPwdForm.password) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      menuList: [],
      activePath: '',
      showUserinfoDialog: false,
      webSocketService: null,
      resetPasswordDialogVisible: false,
      resetPwdForm: {
        password: '',
        confirmPassword: ''
      },
      publicKey: null,
      resetPwdRules: {
        password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, max: 15, message: '密码的长度在6~15个字符之间', trigger: 'blur' },
          {
            pattern: /^[A-Za-z0-9!@#$%^&*()_+={}[\]:;"'<>,.?/~`-]+$/,
            message: '密码只能包含英文字母、数字和特殊字符，不允许中文',
            trigger: 'blur'
          }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapGetters('tabs', ['historyRoutes']),
    ...mapGetters('globalUserInfo', [
      'userId',
      'userAccount',
      'roleId',
      'roleName',
      'isSuperAdmin',
      'isNormalAdmin',
      'hasManagePermission',
      'isAdmin',
      'token'
    ]),

    visibleMenuList() {
      if (!this.menuList || this.menuList.length === 0) return []
      if (this.isAdmin) {
        return this.menuList
      }
      return this.menuList.filter(item => item.title === '首页' || item.title === '设备管理')
    },

    isHomeActive() {
      return this.$route.path === '/welcome'
    },

    currentRoutePath() {
      return this.$route.path
    }
  },
  async created() {
    await this.restoreUserInfoFromSession()
    await this.getMenuList()
    this.activePath = window.sessionStorage.getItem('activePath')
  },

  methods: {
    ...mapMutations('globalUserInfo', ['SET_USER_INFO']),

    // ==================== 缓存 key 映射 ====================
    getCacheKeyByPath(path) {
      const cacheKeyMap = {
        '/batchNo-management': 'batchNoCache',
        '/device-data': 'deviceDataCache',
        '/user-management': 'userManagementCache',
        '/operation-log': 'operationLogCache'
      }
      return cacheKeyMap[path]
    },

    getAllCacheKeys() {
      return [
        'batchNoCache',
        'deviceDataCache',
        'userManagementCache',
        'operationLogCache'
      ]
    },

    restoreUserInfoFromSession() {
      return new Promise((resolve) => {
        const userInfoStr = sessionStorage.getItem('userInfo')
        if (userInfoStr) {
          try {
            const userInfo = JSON.parse(userInfoStr)
            if (!this.userId) {
              this.SET_USER_INFO(userInfo)
            }
            resolve(true)
          } catch (e) {
            console.error('恢复用户信息失败', e)
            resolve(false)
          }
        } else {
          resolve(false)
        }
      })
    },

    closeWebSocket() {
      if (this.webSocketService) {
        this.webSocketService.disconnect()
        this.webSocketService = null
        console.log('WebSocket 连接已关闭')
      }
    },

    // ==================== 串口清理方法 ====================

    /**
     * 清理串口连接 - 通过 Vuex store 断开
     */
    async cleanupSerialConnection() {
      try {
        await this.$store.dispatch('serial/setShouldCleanup', true)
        if (this.$bus) {
          this.$bus.$emit('logout-cleanup')
        }
        await new Promise(resolve => setTimeout(resolve, 500))
        await this.$store.dispatch('serial/fullReset')
        console.log('串口连接已断开并清理完成')
      } catch (error) {
        console.warn('断开串口失败:', error)
        this.$store.dispatch('serial/setGlobalSerialConnected', false)
        this.$store.dispatch('serial/setGlobalSerialPort', null)
      }
    },

    /**
     * 关闭标签页时清理串口连接
     */
    async cleanupSerialOnTabClose() {
      console.log('检测到串口工具标签页被关闭，执行清理...')
      try {
        await this.$store.dispatch('serial/setShouldCleanup', true)
        if (this.$bus) {
          this.$bus.$emit('logout-cleanup')
        }
        await new Promise(resolve => setTimeout(resolve, 300))
        await this.$store.dispatch('serial/fullReset')
        console.log('串口工具标签页关闭，串口已清理')
      } catch (error) {
        console.warn('关闭标签页时清理串口失败:', error)
        this.$store.dispatch('serial/setGlobalSerialConnected', false)
        this.$store.dispatch('serial/setGlobalSerialPort', null)
      }
    },

    /**
     * 判断是否串口工具路径
     */
    isSerialBurnToolPath(path) {
      return path === '/serialBurn-tool'
    },

    // ==================== 用户相关方法 ====================

    async showUserinfo() {
      this.showUserinfoDialog = true
    },

    showResetPasswordDialog() {
      this.resetPasswordDialogVisible = true
    },

    resetPasswordDialogClosed() {
      this.$refs.resetPwdFormRef && this.$refs.resetPwdFormRef.resetFields()
      this.resetPwdForm = {
        password: '',
        confirmPassword: ''
      }
    },

    async fetchPublicKey() {
      try {
        const { data } = await this.$http.get('/rsa/getPublicKey')
        this.publicKey = data
      } catch (error) {
        this.$message.error('操作失败，请刷新重试')
      }
    },

    encryptString(str) {
      const encrypt = new JSEncrypt()
      encrypt.setPublicKey(this.publicKey)
      return encrypt.encrypt(str)
    },

    async confirmResetPassword() {
      this.$refs.resetPwdFormRef.validate(async (valid) => {
        if (!valid) return

        await this.fetchPublicKey()
        const encryptedPassword = this.encryptString(this.resetPwdForm.password)
        const base64EncodedPassword = Base64.encode(encryptedPassword)

        try {
          const { data: result } = await this.$http.post('/user/resetPassword', {
            userId: this.userId,
            password: base64EncodedPassword
          })
          if (result.status === 200) {
            this.$message.success('密码重置成功！')
            this.resetPasswordDialogVisible = false
            this.showUserinfoDialog = false
            setTimeout(() => {
              this.logout()
            }, 1500)
          } else {
            this.$message.error(result.message || '重置密码失败！')
          }
        } catch (error) {
          this.$message.error('重置密码失败，请稍后重试')
        }
      })
    },

    async deleteUserAccount() {
      if (this.userAccount === 'admin') {
        this.$message.warning('admin账号不能被注销')
        return
      }
      const confirmResult = await this.$confirm(
        `确定要注销账号 "${this.userAccount}" 吗？此操作将永久删除该账号，且不可恢复！`,
        '注销账号确认',
        {
          confirmButtonText: '确定注销',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).catch(err => err)
      if (confirmResult !== 'confirm') {
        return
      }
      try {
        const response = await this.$http.delete(`user/delete/${this.userId}`)
        if (response.data && response.data.status === 200) {
          this.$message.success(response.data.message || '账号已注销')
          this.showUserinfoDialog = false
          setTimeout(() => {
            this.logout()
          }, 1500)
        } else {
          var errorMsg = response.data && response.data.message ? response.data.message : '注销失败'
          this.$message.warning(errorMsg)
        }
      } catch (error) {
        console.error('注销账号失败:', error)
        var errMsg = '注销请求失败，请稍后重试'
        if (error.response && error.response.data && error.response.data.message) {
          errMsg = error.response.data.message
        } else if (error.message) {
          errMsg = error.message
        }
        this.$message.error(errMsg)
      }
    },

    formatIconPath(path, width = '20px', height = '20px') {
      const imgPath = require(`@/assets/menu/${path}`)
      const topStyle = 'position: relative; top: 0px;'
      return `<img src="${imgPath}" alt="icon" style="width: ${width}; height: ${height}; ${topStyle}">`
    },

    // ==================== 退出登录 ====================

    /**
     * 退出登录 - 完整清理所有资源
     */
    async logout() {
      const loading = this.$loading({
        lock: true,
        text: '正在退出登录...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        // 1. 断开串口连接（真正关闭物理连接 + 清理界面数据）
        await this.cleanupSerialConnection()

        // 2. 关闭 WebSocket 连接
        this.closeWebSocket()

        // 3. 清除所有会话缓存
        sessionStorage.clear()

        // 4. 清除所有本地存储中的缓存
        this.$store.dispatch('tabs/clearAllPageCache')

        // 5. 清除 Vuex 状态
        this.$store.dispatch('tabs/clearHistory')
        this.$store.dispatch('globalUserInfo/logout')
        this.$store.dispatch('serial/setGlobalSerialConnected', false)
        this.$store.dispatch('serial/setGlobalSerialPort', null)
        this.$store.dispatch('serial/setShouldCleanup', false)

        // 6. 关闭弹窗
        this.showUserinfoDialog = false
        this.resetPasswordDialogVisible = false

        // 7. 跳转到登录页
        this.$router.push('/login')
      } catch (error) {
        console.error('退出登录失败:', error)
        this.$message.error('退出登录失败，请重试')
      } finally {
        loading.close()
      }
    },

    resetDialog() {
      this.showUserinfoDialog = false
    },

    // ==================== 菜单相关 ====================

    getVisibleChildList(childList) {
      if (!childList || childList.length === 0) return []
      if (this.isAdmin) {
        return childList
      }
      return childList.filter(item => item.title === '设备数据')
    },

    async getMenuList() {
      if (this.menuList && this.menuList.length > 0) {
        return
      }
      try {
        const { data: result } = await this.$http.get('/menu/list')
        if (!result.success) {
          this.$message.error(result.message)
          if (result.code === 40102 || result.code === 99999) {
            return this.logout()
          }
        }
        this.menuList = result.data.menuList || []
      } catch (error) {
        console.error('获取菜单列表失败', error)
        this.$message.error('获取菜单列表失败')
      }
    },

    toWelcome() {
      this.$router.push({
        path: '/welcome',
        query: {}
      })
    },

    saveNavState(path) {
      this.activePath = path
      window.sessionStorage.setItem('activePath', path)
      const exists = this.historyRoutes.some(r => r.path === path)
      if (!exists) {
        let title = '未命名'
        let menuItem = this.menuList
          .reduce((acc, item) => acc.concat(item.childList || []), [])
          .find(sub => sub.path === path)
        if (!menuItem) {
          menuItem = this.menuList.find(item => item.path === path)
        }
        title = menuItem && menuItem.title ? menuItem.title : '未命名'
        this.$store.dispatch('tabs/addRoute', {
          path,
          title: title
        })
      }
    },

    navigateToHome() {
      this.$router.push('/welcome')
    },

    // ==================== 历史路由导航方法 ====================

    isRouteActive(route) {
      return this.$route.path === route.path
    },

    navigateToHistory(route) {
      this.$router.push({
        path: route.path
      })
    },

    /**
     * 删除单个历史记录
     * 如果关闭的是串口工具页面，需要断开串口连接并清理
     */
    removeHistoryItem(route) {
      // 1. 保存当前历史记录的副本（在修改之前）
      const historyCopy = [...this.historyRoutes]

      // 2. 获取当前路由在历史记录中的索引
      const currentIndex = historyCopy.findIndex(r => r.path === route.path)
      if (currentIndex === -1) {
        return
      }

      // 3. 检查是否关闭的是串口工具页面
      const isSerialBurnTool = this.isSerialBurnToolPath(route.path)

      // 4. 如果是串口工具页面，先执行清理
      if (isSerialBurnTool) {
        this.cleanupSerialOnTabClose()
      }

      // 5. 从历史记录中移除
      this.$store.dispatch('tabs/removeRoute', route)

      // 6. 同时清除该页面对应的分页缓存
      const cacheKey = this.getCacheKeyByPath(route.path)
      if (cacheKey) {
        sessionStorage.removeItem(cacheKey)
      }

      // 7. 清除串口工具的特殊缓存
      if (isSerialBurnTool) {
        try {
          sessionStorage.removeItem('serialBurnToolCache')
        } catch (e) {
          console.warn('清除串口缓存失败:', e)
        }
      }

      // 8. 如果关闭的是当前页面，需要跳转
      if (this.$route.path === route.path) {
        // 使用副本计算剩余路由
        const remainingRoutes = historyCopy.filter(r => r.path !== route.path)

        if (remainingRoutes.length > 0) {
          let targetRoute
          if (currentIndex < remainingRoutes.length) {
            targetRoute = remainingRoutes[currentIndex]
          } else {
            targetRoute = remainingRoutes[remainingRoutes.length - 1]
          }
          this.$router.push(targetRoute.path)
        } else {
          // 没有剩余页面，跳转到首页
          this.$router.push('/welcome')
        }
      }
    },

    // ==================== 标签页下拉菜单操作 ====================

    handleTabCommand(command) {
      switch (command) {
        case 'closeCurrent':
          this.closeCurrentTab()
          break
        case 'closeOthers':
          this.closeOtherTabs()
          break
        case 'closeAll':
          this.closeAllTabs()
          break
        case 'refresh':
          this.refreshCurrentPage()
          break
      }
    },

    /**
     * 关闭当前标签页
     */
    closeCurrentTab() {
      const currentPath = this.$route.path
      if (currentPath === '/welcome') {
        return
      }

      // 1. 保存当前历史记录的副本（在修改之前）
      const historyCopy = [...this.historyRoutes]

      // 2. 找到当前路由在历史记录中的索引
      const currentIndex = historyCopy.findIndex(r => r.path === currentPath)
      if (currentIndex === -1) {
        return
      }

      const currentRoute = historyCopy[currentIndex]

      // 3. 如果是串口工具页面，先清理
      if (this.isSerialBurnToolPath(currentPath)) {
        this.cleanupSerialOnTabClose()
      }

      // 4. 从历史记录中移除
      this.$store.dispatch('tabs/removeRoute', currentRoute)

      // 5. 清除该页面对应的缓存
      const cacheKey = this.getCacheKeyByPath(currentPath)
      if (cacheKey) {
        sessionStorage.removeItem(cacheKey)
      }

      // 6. 清除串口工具的特殊缓存
      if (this.isSerialBurnToolPath(currentPath)) {
        try {
          sessionStorage.removeItem('serialBurnToolCache')
        } catch (e) {
          console.warn('清除串口缓存失败:', e)
        }
      }

      // 7. 使用副本计算剩余路由（不依赖可能还未更新的 this.historyRoutes）
      const remainingRoutes = historyCopy.filter(r => r.path !== currentPath)

      // 8. 跳转逻辑
      if (remainingRoutes.length > 0) {
        let targetRoute
        // 如果关闭的不是最后一个，跳转到原来当前位置的页面（即后一个页面）
        // 如果关闭的是最后一个，跳转到前一个页面
        if (currentIndex < remainingRoutes.length) {
          targetRoute = remainingRoutes[currentIndex]
        } else {
          targetRoute = remainingRoutes[remainingRoutes.length - 1]
        }
        this.$router.push(targetRoute.path)
      } else {
        // 没有剩余页面，跳转到首页
        this.$router.push('/welcome')
      }
    },

    /**
     * 关闭其他标签页
     */
    closeOtherTabs() {
      const currentPath = this.$route.path
      const currentRoute = this.historyRoutes.find(r => r.path === currentPath)

      if (currentRoute) {
        const otherRoutes = this.historyRoutes.filter(r => r.path !== currentPath)

        // 检查是否有串口工具页面被关闭
        const hasSerialBurnTool = otherRoutes.some(r => this.isSerialBurnToolPath(r.path))
        if (hasSerialBurnTool) {
          this.cleanupSerialOnTabClose()
        }

        // 清除其他页面的缓存
        otherRoutes.forEach(route => {
          const cacheKey = this.getCacheKeyByPath(route.path)
          if (cacheKey) {
            sessionStorage.removeItem(cacheKey)
          }
          // 清除串口工具的特殊缓存
          if (this.isSerialBurnToolPath(route.path)) {
            try {
              sessionStorage.removeItem('serialBurnToolCache')
            } catch (e) {}
          }
        })

        this.$store.commit('tabs/CLEAR_HISTORY')
        this.$store.dispatch('tabs/addRoute', currentRoute)
      } else {
        if (currentPath === '/welcome') {
          this.closeAllTabs()
        } else {
          this.$message.warning('未找到当前页面记录')
        }
      }
    },

    /**
     * 关闭所有标签页
     */
    closeAllTabs() {
      // 检查是否有串口工具页面
      const hasSerialBurnTool = this.historyRoutes.some(r => this.isSerialBurnToolPath(r.path))
      if (hasSerialBurnTool) {
        this.cleanupSerialOnTabClose()
      }

      this.$store.dispatch('tabs/clearHistory')

      this.getAllCacheKeys().forEach(key => {
        sessionStorage.removeItem(key)
      })

      // 清除串口工具的特殊缓存
      try {
        sessionStorage.removeItem('serialBurnToolCache')
      } catch (e) {}

      this.$router.push('/welcome')
    },

    refreshCurrentPage() {
      this.$router.go(0)
    }
  }
}
</script>

<style lang="less" scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.home-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  overflow: auto !important;
  background-color: #f0f2f5;
}

.sidebar {
  width: 150px;
  background-color: black;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100vh;
  overflow: auto !important;
}

.logo-area {
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background-color: black;
  border-bottom: 1px solid #2c2c2c;
}

.logo-img {
  max-width: 250%;
  max-height: 250%;
  object-fit: contain;
}

.sidebar-menu {
  flex: 1;
  border-right: none;
  background-color: black;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

.sidebar-menu::-webkit-scrollbar {
  width: 4px;
}

.sidebar-menu::-webkit-scrollbar-track {
  background: #2c2c2c;
}

.sidebar-menu::-webkit-scrollbar-thumb {
  background: #409EFF;
  border-radius: 2px;
}

.sidebar-menu::-webkit-scrollbar-thumb:hover {
  background: #66b1ff;
}

/deep/ .el-menu-item,
/deep/ .el-submenu__title {
  height: 48px !important;
  line-height: 48px !important;
  display: flex !important;
  align-items: center !important;
}

.menu-title-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 48px; // 固定高度
  overflow: hidden;
}

.menu-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;  // 固定宽度
  height: 24px; // 固定高度
  margin-right: 8px;
  flex-shrink: 0;

  /deep/ img {
    width: 20px !important;
    height: 20px !important;
    object-fit: contain; // 保持比例，不拉伸
    display: block;
  }
}

// 子菜单缩进保持一致
/deep/ .el-menu--inline .el-menu-item {
  padding-left: 36px !important;

  .menu-title-container {
    padding-left: 0;
  }
}

.sidebar-icp {
  padding: 16px 0 20px 0;
  text-align: center;
  border-top: 1px solid #2c2c2c;
  flex-shrink: 0;
  background-color: black;
}

.sidebar-icp a {
  color: #909399;
  font-size: 12px;
  text-decoration: none;
  transition: color 0.3s;
}

.sidebar-icp a:hover {
  color: #409EFF;
  text-decoration: underline;
}

.right-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: auto !important;
}

.right-header {
  height: 48px;
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  display: flex;
  align-items: center;
  padding: 0 20px;
  flex-shrink: 0;
  z-index: 10;
  gap: 12px;
}

.history-tabs-wrapper {
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.history-tabs-wrapper::-webkit-scrollbar {
  height: 3px;
}

.history-tabs-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.history-tabs-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.history-tabs {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 2px;
}

.tab-separator {
  color: #dcdfe6;
  font-size: 14px;
  margin: 0 4px;
  user-select: none;
  flex-shrink: 0;
  font-weight: 300;
}

.history-tab {
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 12px;
  cursor: pointer;
  transition: all 0.3s;
  border-bottom: 2px solid transparent;
  background-color: #fff;
  flex-shrink: 0;
}

.history-tab:hover {
  background-color: #f5f7fa;
}

.history-tab:hover .tab-close {
  opacity: 1;
}

.history-tab.active {
  color: #409EFF;
  border-bottom-color: #409EFF;
}

.home-tab.active {
  color: #409EFF;
  border-bottom-color: #409EFF;
}

.tab-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  white-space: nowrap;
}

.tab-title {
  font-size: 14px;
}

.tab-close {
  opacity: 0;
  transition: opacity 0.3s;
  color: #909399;
  font-size: 12px;
  display: flex;
  align-items: center;
  width: 16px;
  height: 16px;
  border-radius: 2px;
}

.tab-close:hover {
  color: #f56c6c;
  background-color: rgba(0, 0, 0, 0.1);
}

.tab-dropdown {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-left: 4px;
}

.tab-dropdown-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  color: #606266;
  font-size: 18px;
}

.tab-dropdown-trigger:hover {
  background-color: #f5f7fa;
  color: #409EFF;
}

.tab-dropdown-menu {
  min-width: 120px !important;
}

.tab-dropdown-menu .el-dropdown-menu__item {
  font-size: 13px;
  padding: 0 16px;
  line-height: 34px;
}

.tab-dropdown-menu .el-dropdown-menu__item i {
  margin-right: 8px;
  font-size: 14px;
}

.tab-dropdown-menu .el-dropdown-menu__item--divided {
  border-top: 1px solid #e4e7ed;
  margin-top: 5px;
  margin-bottom: 0;
  padding-top: 5px;
  position: relative;
}

.tab-dropdown-menu .el-dropdown-menu__item--divided::before {
  display: none;
}

.avatar-button {
  background-color: transparent !important;
  border: none !important;
  padding: 0 !important;
  cursor: pointer;
  flex-shrink: 0;
}

.avatar-button img {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  object-fit: cover;
}

.right-content {
  flex: 1;
  position: relative;
  overflow: auto !important;
  background-color: #f0f2f5;
}

.dialog-title-custom {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
}

.logout-title-btn {
  color: #f56c6c !important;
  font-size: 14px;
  padding: 0 !important;
}

.logout-title-btn:hover {
  color: #ff8080 !important;
  background-color: transparent !important;
}

.dialog-content {
  text-align: center;
}

.avatar-large {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 16px;
}

.user-account-text {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 8px 0;
}

.user-role-text {
  font-size: 14px;
  color: #909399;
  margin: 0 0 20px 0;
}

.button-group {
  margin-top: 10px;
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.btn-padding {
  padding: 8px 16px !important;
}

.reset-pwd-form .pwd-form-item {
  margin-bottom: 28px;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 10px 0 20px 0;
}

.dialog-footer .cancel-btn,
.dialog-footer .confirm-btn {
  padding: 10px 28px !important;
  font-size: 14px;
}
</style>
